import { FC, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { DigitalFormsQueries as queryEndpointKeys } from '@frontend/api-digital-forms';
import { DigitalFormScopeHooks } from '@frontend/digital-forms-scope';
import { useTranslation } from '@frontend/i18n';
import { Modal } from '@frontend/design-system';
import { pendoTags } from '../../constants';
import { useGeneratedLink, useSendDocument, useSteps } from '../../hooks';
import { useSendFormsContext, useSendFormsStore } from '../../providers';
import { SendFormsOrigin, SendFormStep } from '../../types';
import { DigitalFormsSendingExperienceTracker } from '../../utils';
import {
  GetPendoTagForBackButtonFn,
  GetPendoTagForNextButtonFn,
  ShouldShowBackButtonFn,
  ShouldShowNextButtonFn,
} from './footer.types';

const Footer: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const { sendFormsModalControls, origin } = useSendFormsContext();
  const {
    currentStep,
    isSending,
    isGeneratingSolicitedLink,
    solicitedLink,
    email,
    emailBody,
    emailSubject,
    selectedDeliveryMethod,
    clearStore,
    isUploadInProgress,
    formOrPacketToSend,
    areFormsEnabledLocationIdsSet,
    setFormOrPacketToSend,
    locationId,
  } = useSendFormsStore([
    'currentStep',
    'isSending',
    'isGeneratingSolicitedLink',
    'solicitedLink',
    'email',
    'emailBody',
    'emailSubject',
    'selectedDeliveryMethod',
    'clearStore',
    'isUploadInProgress',
    'formOrPacketToSend',
    'areFormsEnabledLocationIdsSet',
    'setFormOrPacketToSend',
    'locationId',
  ]);
  const { sendDocument } = useSendDocument();
  const { generateLink } = useGeneratedLink();
  const { goToNextStep, goBackAStep, isLastStep, isFirstStep } = useSteps();
  const { hasSendFormsAndPacketsACL } = DigitalFormScopeHooks.useFormsACL({ locationId });
  const isSelectAppointmentStep = currentStep === SendFormStep.SELECT_APPOINTMENT;
  const _isUploadInProgress = false;
  const queryClient = useQueryClient();

  async function goToNextStepHandler() {
    if (isSending) {
      return;
    }

    switch (currentStep) {
      case SendFormStep.SELECTED_MESSAGE_MODE:
        sendDocument();
        return;

      case SendFormStep.SELECT_APPOINTMENT:
        if (origin === SendFormsOrigin.KIOSK && selectedDeliveryMethod) {
          if (!solicitedLink) {
            const { success } = await generateLink(selectedDeliveryMethod);
            if (!success) return;
          }
          goToNextStep();
          return;
        }
        goToNextStep();
        break;

      default:
        goToNextStep();
        break;
    }
  }

  function goBackAStepHandler() {
    if (isSending) {
      return;
    }

    switch (currentStep) {
      case SendFormStep.SELECT_DELIVERY_METHOD:
        if (solicitedLink) {
          // Prevent user from going back to the appointment step if solicited link is generated
          return;
        }
        goBackAStep();
        break;

      case SendFormStep.SELECT_APPOINTMENT:
        goBackAStep();
        break;

      case SendFormStep.SELECT_DOCUMENT:
        setFormOrPacketToSend(undefined);
        goBackAStep();
        break;

      default:
        goBackAStep();
        break;
    }
  }

  function cancelClickHandler() {
    if (isSending) {
      return;
    }

    DigitalFormsSendingExperienceTracker.finishSendFormTracking({
      isComplete: false,
      origin,
      step: currentStep,
    });
    sendFormsModalControls.closeModal();
    clearStore(origin);
    queryClient.invalidateQueries(queryEndpointKeys.endpointKeys.submissionDetailsForPerson);
  }

  function shouldDisableBackButton(): boolean {
    if (currentStep === SendFormStep.SELECT_DELIVERY_METHOD) {
      // Prevent user from going back to the appointment step if solicited link is generated
      return !!solicitedLink;
    }

    return isGeneratingSolicitedLink || isSending || _isUploadInProgress;
  }

  function shouldDisableCancelButton(): boolean {
    if (isUploadInProgress) {
      return true;
    }

    return isGeneratingSolicitedLink || isSending;
  }

  function shouldDisableNextButton(): boolean {
    if (isUploadInProgress || !areFormsEnabledLocationIdsSet) {
      return true;
    }

    switch (currentStep) {
      case SendFormStep.SELECT_LOCATION:
        return !(locationId && hasSendFormsAndPacketsACL);

      case SendFormStep.SELECTED_MESSAGE_MODE:
        return !(email && emailBody && emailSubject);

      case SendFormStep.SELECT_DOCUMENT:
        return !formOrPacketToSend;

      default:
        return false;
    }
  }

  const shouldShowBackButton = useCallback<ShouldShowBackButtonFn>(() => {
    if (isFirstStep()) {
      return false;
    }

    return true;
  }, [isFirstStep]);

  const shouldShowNextButton = useCallback<ShouldShowNextButtonFn>(({ currentStep, deliveryMethod }) => {
    if (currentStep === SendFormStep.SELECT_DELIVERY_METHOD) {
      return false;
    }

    if (currentStep === SendFormStep.SELECTED_MESSAGE_MODE) {
      if (deliveryMethod === 'kiosk') {
        return false;
      }
    }

    return true;
  }, []);

  const getPendoTagForNextButton = useCallback<GetPendoTagForNextButtonFn>(
    (step: SendFormStep) => {
      if (isLastStep()) {
        return pendoTags.send;
      }

      switch (step) {
        case SendFormStep.SELECT_LOCATION:
          return pendoTags.steps.location.next;

        case SendFormStep.SELECT_APPOINTMENT:
          return pendoTags.steps.appointments.next;

        case SendFormStep.SELECT_DOCUMENT:
          return pendoTags.steps.documentSelection.next;

        default:
          return '';
      }
    },
    [isLastStep]
  );

  const getPendoTagForBackButton = useCallback<GetPendoTagForBackButtonFn>((step: SendFormStep) => {
    switch (step) {
      case SendFormStep.SELECT_APPOINTMENT:
        return pendoTags.steps.appointments.back;

      case SendFormStep.SELECT_DELIVERY_METHOD:
        return pendoTags.steps.deliveryMethod.back;

      case SendFormStep.SELECT_DOCUMENT:
        return pendoTags.steps.documentSelection.back;

      default:
        return '';
    }
  }, []);

  return (
    <Modal.Footer
      primary={
        shouldShowNextButton({ currentStep, deliveryMethod: selectedDeliveryMethod })
          ? {
              label: isLastStep() ? t('Send') : t('Next'),
              disabled: shouldDisableNextButton(),
              onClick: goToNextStepHandler,
              trackingId: getPendoTagForNextButton(currentStep),
            }
          : undefined
      }
      secondary={
        !isSelectAppointmentStep
          ? {
              label: isLastStep() && selectedDeliveryMethod === 'kiosk' ? t('Close') : t('Cancel'),
              disabled: shouldDisableCancelButton(),
              onClick: cancelClickHandler,
              trackingId: isLastStep() && selectedDeliveryMethod === 'kiosk' ? pendoTags.close : pendoTags.cancel,
            }
          : undefined
      }
      tertiary={
        shouldShowBackButton()
          ? {
              disabled: shouldDisableBackButton(),
              onClick: goBackAStepHandler,
              trackingId: getPendoTagForBackButton(currentStep),
              label: t('Back'),
              iconName: 'back-small',
            }
          : undefined
      }
    />
  );
};

const SendFormsFooter: FC = () => {
  const { currentStep } = useSendFormsStore(['currentStep']);

  if (currentStep === SendFormStep.SELECT_CONTACT) {
    return null;
  }

  return <Footer />;
};

export default SendFormsFooter;
